import { colors } from '@astro-ui/fondations'
import type { SavedSelectedOptionsType } from 'features/SCMS/redux/forecastFilter/forecastFilterSlice'

import type { ForecastProductListParamsType } from '../@types/forecastFilterTypes'
import { DailyDistributionsRowConfigType } from '../@types/dpTypes'
import { formatGap, formatPercent } from '../helper/dailyDistributions'

export const FORECAST_FILTER_STORAGE_KEY = 'scms_dp_forecast_filters'
export const FORECAST_SELECTED_PRODUCT_IDS_STORAGE_KEY = 'scms_dp_forecast_selected_product_ids'
export const FORECAST_SELECTED_PRODUCTS_STORAGE_KEY = 'scms_dp_forecast_selected_products'
export const SKU_REVIEW_KEYWORD_STORAGE_KEY = 'scms_dp_sku_review_keyword'

export const INITIAL_FILTERS: ForecastProductListParamsType = {
  hub_id: '',
  keyword: '',
  product_ids: '',
  product_names: '',
  need_review: 'false',
  remark: '',
  l1_category_ids: '',
  l2_category_ids: '',
  forecast_classification: '',
  pareto_class: '',
  product_types: '',
  brand_ids: '',
  pic_emails: '',
  business_line_keys: '',
  hardcode_reason: '',
  source_status: '',
  fc_uplift_range: '14',
  swls_range: '7',
  order_by: '',
  order_direction: 'asc',
  cursor: '',
  page_size: 100,
}

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export const MONTH_NAMES = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
]

export const DAILY_DISTRIBUTIONS_ROW_CONFIG: DailyDistributionsRowConfigType[] = [
  { label: 'Breakdown FC', isHeader: true, bgColor: colors.lightBlue },
  { label: ' • Baseline', key: 'baseline' },
  { label: ' • Price Change', key: 'price_cut' },
  { label: ' • Campaign', key: 'campaign' },
  { label: ' • Festive', key: 'festive' },
  { label: 'Forecast', key: 'forecast' },
  { label: 'Sales with Loss Sales', key: 'sales_with_loss_sales', bgColor: colors.bgDisabled },
  { label: 'Gap (pcs)', key: 'gap_pcs', formatter: formatGap, bgColor: colors.bgDisabled },
  { label: 'Gap (%)', key: 'gap_percentage', formatter: formatPercent, bgColor: colors.bgDisabled },
  { label: 'Impacted Hub', key: 'impacted_hub', bgColor: colors.bgDisabled },
  { label: 'Hub Distribution', key: 'hub_distribution', bgColor: colors.bgDisabled },
  { label: 'Actual Sales', key: 'actual_sales', bgColor: colors.bgDisabled },
  { label: 'Loss Sales', key: 'loss_sales', bgColor: colors.bgDisabled },
]

export const ACTIVE_FILTER_PILLS: Array<{
  key: keyof ForecastProductListParamsType
  label: string
  resolveLabel?: (val: string, saved: SavedSelectedOptionsType | null) => string
}> = [
  { key: 'product_ids', label: 'Product ID' },
  { key: 'product_names', label: 'Product Name' },
  { key: 'keyword', label: 'Keyword' },
  {
    key: 'l1_category_ids',
    label: 'L1',
    resolveLabel: (val, saved) =>
      val
        .split(',')
        .filter(Boolean)
        .map((id) => saved?.l1.find((o) => String(o.value) === id)?.label ?? id)
        .join(', '),
  },
  {
    key: 'l2_category_ids',
    label: 'L2',
    resolveLabel: (val, saved) =>
      val
        .split(',')
        .filter(Boolean)
        .map((id) => saved?.l2.find((o) => String(o.value) === id)?.label ?? id)
        .join(', '),
  },
  {
    key: 'brand_ids',
    label: 'Brand',
    resolveLabel: (val, saved) =>
      val
        .split(',')
        .filter(Boolean)
        .map((id) => saved?.brand.find((o) => String(o.value) === id)?.label ?? id)
        .join(', '),
  },
  { key: 'forecast_classification', label: 'Forecast Classification' },
  { key: 'pareto_class', label: 'Pareto' },
  { key: 'product_types', label: 'Product Type' },
  {
    key: 'pic_emails',
    label: 'PIC',
    resolveLabel: (val, saved) =>
      val
        .split(',')
        .filter(Boolean)
        .map((email) => saved?.pic.find((o) => String(o.value) === email)?.label ?? email)
        .join(', '),
  },
  { key: 'business_line_keys', label: 'Business Line' },
  { key: 'hardcode_reason', label: 'Hardcode Reason' },
  {
    key: 'remark',
    label: 'Remark',
    resolveLabel: (val) => {
      if (val === 'true') return 'Hardcoded'
      if (val === 'false') return 'Non Hardcoded'
      return val
    },
  },
  { key: 'source_status', label: 'Source' },
]

export const FORECAST_LEGENDS = [
  { label: 'Under', value: 'Under Forecast', bgColor: '#FEEDEF', borderColor: '#C73E4F' },
  { label: 'Over', value: 'Over Forecast', bgColor: '#FFF1CE', borderColor: '#CC960A' },
  { label: 'Ok', value: 'Ok', bgColor: '#E6F7EF', borderColor: '#008849' },
]

export const FORECAST_CATEGORIES = [
  { label: 'All SKUs', value: 'false' },
  { label: 'To Review', value: 'true' },
]

export const FORECAST_SCROLLABLE_COLUMNS = [
  {
    id: 'avgSls',
    label: 'AVG SLS',
    sortable: false,
    filterable: true,
    width: '120px',
    minWidth: '120px',
  },
  {
    id: 'avgFc',
    label: 'AVG FC',
    sortable: false,
    filterable: true,
    width: '120px',
    minWidth: '120px',
  },
  {
    id: 'gapPcs',
    label: 'Gap (pcs)',
    sortable: true,
    filterable: false,
    width: '100px',
    minWidth: '100px',
  },
  {
    id: 'gapPct',
    label: 'Gap %',
    sortable: true,
    filterable: false,
    width: '100px',
    minWidth: '100px',
  },
  {
    id: 'impHub',
    label: 'Imp. Hub',
    sortable: false,
    filterable: false,
    width: '80px',
    minWidth: '80px',
  },
  {
    id: 'impHubCodes',
    label: 'Imp. Hub Codes',
    sortable: false,
    filterable: false,
    width: '150px',
    minWidth: '150px',
  },
  {
    id: 'impHubIds',
    label: 'Imp. Hub IDs',
    sortable: false,
    filterable: false,
    width: '150px',
    minWidth: '150px',
  },
  {
    id: 'avgActual',
    label: 'AVG Actual',
    sortable: false,
    filterable: false,
    width: '120px',
    minWidth: '120px',
  },
  {
    id: 'avgPrice',
    label: 'AVG Price',
    sortable: false,
    filterable: false,
    width: '120px',
    minWidth: '120px',
  },
  { id: 'avgDiscount', label: 'AVG Discount', sortable: false, filterable: false, width: '130px' },
  {
    id: 'harcodeReason',
    label: 'Hardcode Reason',
    sortable: false,
    filterable: false,
    width: '150px',
    minWidth: '150px',
  },
  { id: 'pareto', label: 'Pareto', sortable: false, filterable: false, width: '100px' },
  { id: 'l1', label: 'L1', sortable: false, filterable: false, width: '200px', minWidth: '200px' },
  { id: 'l2', label: 'L2', sortable: false, filterable: false, width: '200px', minWidth: '200px' },
  { id: 'source', label: 'Source', sortable: false, filterable: false, width: '130px' },
  {
    id: 'businessLines',
    label: 'Business Lines',
    sortable: false,
    filterable: false,
    width: '200px',
  },
  {
    id: 'lastUpdatedAt',
    label: 'Last Updated At',
    sortable: false,
    filterable: false,
    width: '150px',
    minWidth: '150px',
    info: 'Timestamp when this forecast value was last refreshed in SCMS, after DAG processing completes.',
  },
  {
    id: 'pic',
    label: 'PIC',
    sortable: false,
    filterable: false,
    width: '200px',
    minWidth: '250px',
  },
]

export const FORECAST_SCROLLABLE_GRID_TEMPLATE = FORECAST_SCROLLABLE_COLUMNS.map(
  (col) => col.width,
).join(' ')

export const TREND_LINE_CONFIG = [
  {
    key: 'baseline',
    label: 'Baseline',
    color: '#246EE5',
    bgColor: '#E9F1FC',
    textColor: '#0B2145',
    axisId: 'left',
  },
  {
    key: 'baseline_uplift',
    label: 'Baseline + Uplift',
    color: '#00AA5B',
    bgColor: '#E6F7EF',
    textColor: '#00331B',
    axisId: 'left',
  },
  {
    key: 'actual_sales',
    label: 'Actual Sales',
    color: '#9500CA',
    bgColor: '#F6E6FC',
    textColor: '#310043',
    axisId: 'left',
  },
  {
    key: 'loss_sales',
    label: 'Loss Sales',
    color: '#E04559',
    bgColor: '#FEEDEF',
    textColor: '#4B171E',
    axisId: 'left',
  },
  {
    key: 'actual_with_loss_sales',
    label: 'Actual + Loss Sales',
    color: '#F59E0B',
    bgColor: '#FFF3E6',
    textColor: '#4C2800',
    axisId: 'left',
  },
  {
    key: 'last_year_actual_sales',
    label: 'Last Year Actual Sales',
    color: '#6847BB',
    bgColor: '#F0EDF8',
    textColor: '#1F1538',
    axisId: 'left',
  },
  { key: 'price_sum', label: 'Selling Price', color: '#46AEB8', axisId: 'right' },
  { key: 'discount_sum', label: 'Discount Rate', color: '#92b7f2', axisId: 'right' },
]

export const TREND_LEFT_LINES = TREND_LINE_CONFIG.filter((c) => c.axisId === 'left')
export const TREND_RIGHT_LINES = TREND_LINE_CONFIG.filter((c) => c.axisId === 'right')

export const FORECAST_GAP_COLOR_THEME = {
  underforecast: {
    state: 'Undercast',
    borderColor: '#C73E4F',
    bgColor: '#FEEDEF',
    stateColor: '#641F28',
    valueColor: '#F94D63',
  },
  overforecast: {
    state: 'Overforecast',
    borderColor: '#CC960A',
    bgColor: '#FFF1CE',
    stateColor: '#664B05',
    valueColor: '#CC960A',
  },
  ok: {
    state: 'OK',
    borderColor: '#008849',
    bgColor: '#E6F7EF',
    stateColor: '#004424',
    valueColor: '#008849',
  },
}

export const GapConditionOptions = [
  {
    label: 'more than or equal to',
    value: 'gte',
  },
  {
    label: 'less than or equal to',
    value: 'lte',
  },
]
